import type { StateCreator } from 'zustand';

import { MobileTab } from '@/constants/enums';

import type { IWardrobeStore } from './types';

const initialUi: IWardrobeStore['ui'] = {
  activeTab: 'my-outfits' as const,
  mobileActiveTab: MobileTab.Character,
  isPanelCollapsed: { left: false, right: false },
  bottomSheetHeight: 'closed' as const,
  bottomSheetTab: 'character' as const,
  composerTab: 'character' as const,
  historySheetOpen: false,
  unseenHistoryCount: 0,
  expandedSectionId: 'wardrobe:complete-outfits' as string | null,
};

/**
 * UI slice — purely visual / navigational state (tabs, panels, sheets, etc.).
 *
 * Note: `setHistorySheetOpen` is the only non-pure-UI action here — when the
 * sheet opens it also resets `generation.viewMode` to 'standard' so reopening
 * the panel doesn't strand the user in a compare/grid view that doesn't
 * apply to the history list. That cross-slice write is intentional and kept
 * here because the trigger is UI; the slice `set` accepts whole-store writes.
 */
export type UiSliceContribution = Pick<
  IWardrobeStore,
  'ui' | 'setActiveTab' | 'setMobileTab' | 'togglePanel' | 'setBottomSheetHeight' | 'setBottomSheetTab' | 'setComposerTab' | 'collapseBottomSheet' | 'setHistorySheetOpen' | 'setExpandedSection'
>;

export const createUiSlice: StateCreator<IWardrobeStore, [], [], UiSliceContribution> = set => ({
  ui: initialUi,

  setActiveTab: tab => set(s => ({ ui: { ...s.ui, activeTab: tab } })),
  setMobileTab: tab => set(s => ({ ui: { ...s.ui, mobileActiveTab: tab } })),

  togglePanel: side =>
    set(s => ({
      ui: {
        ...s.ui,
        isPanelCollapsed: {
          ...s.ui.isPanelCollapsed,
          [side]: !s.ui.isPanelCollapsed[side],
        },
      },
    })),

  setBottomSheetHeight: height => set(s => ({ ui: { ...s.ui, bottomSheetHeight: height } })),
  setBottomSheetTab: tab => set(s => ({ ui: { ...s.ui, bottomSheetTab: tab } })),
  setComposerTab: tab => set(s => ({ ui: { ...s.ui, composerTab: tab } })),
  collapseBottomSheet: () => set(s => ({ ui: { ...s.ui, bottomSheetHeight: 'closed' } })),

  setHistorySheetOpen: open =>
    set(s => ({
      ui: {
        ...s.ui,
        historySheetOpen: open,
        bottomSheetHeight: open ? 'closed' : s.ui.bottomSheetHeight,
        unseenHistoryCount: open ? 0 : s.ui.unseenHistoryCount,
      },
      generation: open ? { ...s.generation, viewMode: 'standard' as const } : s.generation,
    })),

  setExpandedSection: id => set(s => ({ ui: { ...s.ui, expandedSectionId: id } })),
});
